import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Chip, // Added Chip import
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Category } from '../../types/Category';
import api from '../../services/api';
import AiSparklesIcon from '../icons/AiSparklesIcon';
import { useSearchSettings } from '../../contexts/SearchSettingsContext';
import { FEATURE_FLAG_KEYS, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useHelpContent } from '../../utils/HelpUtils';
import { normalizeCategoryName, invertSubcaseSelection } from '../../utils/CategoryUtils';
import { contentSearchablePlainText } from '../../utils/contentRichPreviewHtml';
import { fetchCategoryWithChildren } from '../../services/CategoryService';
import { InvertSelectionButton } from '../category/CategoryContent';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * Tooltips stay inside the panel so they unmount with Find (no orphaned portal poppers).
 * Non-interactive so an open tooltip cannot intercept clicks on checkboxes/radios below it.
 */
const FindTooltip: React.FC<React.ComponentProps<typeof Tooltip>> = ({
  children,
  PopperProps,
  ...props
}) => (
  <Tooltip
    {...props}
    disableInteractive
    PopperProps={{
      ...PopperProps,
      disablePortal: true,
    }}
  >
    {children}
  </Tooltip>
);

type FindResult = {
  category: Category;
  matchedContents?: any[];
  score?: number;
};

interface FindCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  selectedCategory: Category | null;
  /** When set, text find starts at/after this content row within the selected category. */
  selectedContentId?: string | null;
  onCategorySelect: (category: Category) => void;
  onExpandParents: (category: Category) => void;
  findCategoryInTree: (categories: Category[], targetId: string) => Category | null;
  onContentSelect?: (categoryId: string, contentId: string, searchString: string, searchTerms: string[], searchMode: 'any' | 'all' | 'exact' | 'semantic', wholeWord: boolean, caseSensitive: boolean, allMatchingContentIds?: string[]) => void;
  subcaseGroups?: { name: string; subcases: string[] }[];
  documentSubcases?: string[];
  documentName?: string;
}

const FindCategoryDialog: React.FC<FindCategoryDialogProps> = ({
  open,
  onClose,
  categories,
  selectedCategory,
  selectedContentId = null,
  onCategorySelect,
  onExpandParents,
  findCategoryInTree,
  onContentSelect,
  subcaseGroups = [],
  documentSubcases = [],
  documentName,
}) => {
  const theme = useTheme();
  const { settings, updateSettings } = useSearchSettings();
  const { findShowSimilarityScore, hybridSemanticRanking } = settings;
  const { getHelpText, getTooltipTextForState } = useHelpContent();
  const { isEnabled } = useFeatureFlags();
  const semanticFindEnabled = isEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND);
  
  // Find feature state
  const [findSearchString, setFindSearchString] = useState('');
  const [findResults, setFindResults] = useState<FindResult[]>([]);
  const [currentFindIndex, setCurrentFindIndex] = useState(0);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [findStarted, setFindStarted] = useState(false);
  const [showEndWarning, setShowEndWarning] = useState(false);
  const [showStartWarning, setShowStartWarning] = useState(false);
  const [hasWrappedAround, setHasWrappedAround] = useState(false);
  const [searchInDescriptions, setSearchInDescriptions] = useState(false);
  const [searchInNames, setSearchInNames] = useState(true); // Default to true since names are usually the most important
  const [searchMode, setSearchMode] = useState<'any' | 'all' | 'exact' | 'semantic'>('any');
  const [wholeWord, setWholeWord] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [searchInContent, setSearchInContent] = useState(true);
  // Add a state to store the current search's matching content IDs
  const [currentSearchMatchingIds, setCurrentSearchMatchingIds] = useState<string[]>([]);
  
  // Subcase filtering state (same model as Full-tree view: one list + active group chips)
  const [filterBySubcases, setFilterBySubcases] = useState(false);
  const [selectedSubcaseGroups, setSelectedSubcaseGroups] = useState<string[]>([]);
  const [selectedIndividualSubcases, setSelectedIndividualSubcases] = useState<string[]>([]);
  const findFilterSubcasesBaselineRef = useRef<string[] | null>(null);

  // UI state
  const [searchOptionsCollapsed, setSearchOptionsCollapsed] = useState(false);
  const [subcaseSelectionCollapsed, setSubcaseSelectionCollapsed] = useState(false);
  const [isSemanticSearching, setIsSemanticSearching] = useState(false);
  const [isTextSearching, setIsTextSearching] = useState(false);
  
  // Dialog position and drag state
  const [dialogPosition, setDialogPosition] = useState({ x: window.innerWidth - 350, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Helper function to parse search terms based on mode
  const parseSearchTerms = (searchString: string): string[] => {
    if (searchMode === 'exact') {
      return [searchString];
    }
    // Split by whitespace for 'any' and 'all' modes
    return searchString.split(/\s+/).filter(term => term.length > 0);
  };
  
  // Helper function to check if text matches search terms
  const checkTextMatch = (text: string, searchTerms: string[]): boolean => {
    // For case-sensitive search, use original text; otherwise convert to lowercase
    const searchText = caseSensitive ? text : text.toLowerCase();
    
    if (searchMode === 'exact') {
      const searchTerm = searchTerms[0];
      if (wholeWord) {
        // Unicode-aware word boundary
        const searchPattern = caseSensitive ? searchTerm : searchTerm.toLowerCase();
        const escapedPattern = searchPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordBoundary = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapedPattern}(?=[^\\p{L}\\p{N}]|$)`, caseSensitive ? 'gu' : 'giu');
        return wordBoundary.test(searchText);
      } else {
        const searchPattern = caseSensitive ? searchTerm : searchTerm.toLowerCase();
        return searchText.includes(searchPattern);
      }
    }
    
    if (searchMode === 'all') {
      // All terms must be found
      return searchTerms.every(term => {
        if (wholeWord) {
          const searchPattern = caseSensitive ? term : term.toLowerCase();
          const escapedPattern = searchPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const wordBoundary = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapedPattern}(?=[^\\p{L}\\p{N}]|$)`, caseSensitive ? 'gu' : 'giu');
          return wordBoundary.test(searchText);
        } else {
          const searchPattern = caseSensitive ? term : term.toLowerCase();
          return searchText.includes(searchPattern);
        }
      });
    }
    
    // 'any' mode - at least one term must be found
    return searchTerms.some(term => {
      if (wholeWord) {
        const searchPattern = caseSensitive ? term : term.toLowerCase();
        const escapedPattern = searchPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordBoundary = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapedPattern}(?=[^\\p{L}\\p{N}]|$)`, caseSensitive ? 'gu' : 'giu');
        return wordBoundary.test(searchText);
      } else {
        const searchPattern = caseSensitive ? term : term.toLowerCase();
        return searchText.includes(searchPattern);
      }
    });
  };

  const subcasesUnionForSubcaseGroupNames = (names: string[]) => {
    const acc = new Set<string>();
    names.forEach(n => {
      const g = subcaseGroups.find(x => x.name === n);
      g?.subcases.forEach(s => acc.add(s));
    });
    return acc;
  };

  const handleFindSubcaseGroupSelect = (groupName: string) => {
    const group = subcaseGroups.find(g => g.name === groupName);
    if (!group) return;

    const allDocSubcasesSelected = (selected: string[]) =>
      documentSubcases.length > 0 &&
      selected.length === documentSubcases.length &&
      documentSubcases.every(s => selected.includes(s));

    setSelectedSubcaseGroups(prevNames => {
      const turningOff = prevNames.includes(groupName);
      const nextNames = turningOff
        ? prevNames.filter(n => n !== groupName)
        : [...prevNames, groupName];

      setSelectedIndividualSubcases(prevSub => {
        if (!turningOff) {
          if (prevNames.length === 0) {
            findFilterSubcasesBaselineRef.current = [...prevSub];
          }
          if (allDocSubcasesSelected(prevSub) && prevNames.length === 0) {
            return [...group.subcases];
          }
          const merged = [...prevSub];
          for (const s of group.subcases) {
            if (merged.indexOf(s) === -1) merged.push(s);
          }
          return merged;
        }
        const cover = subcasesUnionForSubcaseGroupNames(nextNames);
        const filtered = prevSub.filter(s => !group.subcases.includes(s) || cover.has(s));
        if (nextNames.length === 0) {
          if (findFilterSubcasesBaselineRef.current !== null) {
            const restored = [...findFilterSubcasesBaselineRef.current];
            findFilterSubcasesBaselineRef.current = null;
            return restored;
          }
          return filtered.length === 0 ? [...documentSubcases] : filtered;
        }
        return filtered;
      });

      return nextNames;
    });
  };

  /** Effective subcase filter list (group chips keep this in sync, same as Full-tree view). */
  const getSelectedSubcases = (): string[] => selectedIndividualSubcases;

  // Helper function to check if content should be included based on subcase filter
  const shouldIncludeContent = (content: any): boolean => {
    if (!filterBySubcases) return true;

    const selectedSubcases = getSelectedSubcases();
    if (selectedSubcases.length === 0) return false;

    return selectedSubcases.includes(content.subcase);
  };

  /** Preorder (document) order of category ids under {@code root}. */
  const flattenCategoryIdsPreorder = (root: Category): string[] => {
    const ids: string[] = [root.id];
    for (const child of root.children ?? []) {
      ids.push(...flattenCategoryIdsPreorder(child));
    }
    return ids;
  };

  /**
   * Index of the first result at or after the current selection (document order).
   * {@code fromContentIndex} is the selected row’s index in its category’s content list
   * (from the live pane), so we do not depend on id identity across a full-tree refetch.
   * If nothing follows the selection, wraps to the first result.
   */
  const findStartPosition = (
    results: FindResult[],
    searchTree: Category,
    fromCategoryId: string | null,
    fromContentIndex: number,
  ): { findIndex: number; contentIndex: number; wrapped: boolean } => {
    if (!fromCategoryId || results.length === 0) {
      return { findIndex: 0, contentIndex: 0, wrapped: false };
    }
    const order = flattenCategoryIdsPreorder(searchTree);
    const selectedPos = order.indexOf(fromCategoryId);
    if (selectedPos < 0) {
      return { findIndex: 0, contentIndex: 0, wrapped: false };
    }

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const catPos = order.indexOf(result.category.id);
      if (catPos < selectedPos) continue;
      if (catPos > selectedPos) {
        return { findIndex: i, contentIndex: 0, wrapped: false };
      }
      const matched = result.matchedContents;
      if (!matched || matched.length === 0) {
        return { findIndex: i, contentIndex: 0, wrapped: false };
      }
      // No specific row selected — start at the first match in this category.
      if (fromContentIndex < 0) {
        return { findIndex: i, contentIndex: 0, wrapped: false };
      }
      const contents = result.category.contents ?? [];
      for (let j = 0; j < matched.length; j++) {
        const mPos = contents.findIndex((c) => c.id === matched[j].id);
        if (mPos >= fromContentIndex) {
          return { findIndex: i, contentIndex: j, wrapped: false };
        }
      }
    }
    return { findIndex: 0, contentIndex: 0, wrapped: true };
  };

  /** Snapshot of the pane selection when Find is run (stable across async tree fetch). */
  const captureSearchOrigin = (): {
    categoryId: string | null;
    contentIndex: number;
    contentId: string | null;
  } => {
    const categoryId = selectedCategory?.id ?? null;
    const contentId = selectedContentId ?? null;
    if (!categoryId || !contentId) {
      return { categoryId, contentIndex: -1, contentId };
    }
    const live =
      findCategoryInTree(categories, categoryId) ?? selectedCategory;
    let contents = live?.contents ?? selectedCategory?.contents ?? [];
    let contentIndex = contents.findIndex((c) => c.id === contentId);
    // Pane selection can outlive a stale selectedCategory object without contents.
    if (contentIndex < 0) {
      const findIndexInTree = (cats: Category[]): number => {
        for (const cat of cats) {
          if (cat.id === categoryId && cat.contents) {
            const idx = cat.contents.findIndex((c) => c.id === contentId);
            if (idx >= 0) return idx;
          }
          if (cat.children?.length) {
            const found = findIndexInTree(cat.children);
            if (found >= 0) return found;
          }
        }
        return -1;
      };
      contentIndex = findIndexInTree(categories);
    }
    return { categoryId, contentIndex, contentId };
  };

  /** Collect matches in document (preorder) order under {@code root}. */
  const collectMatchesInDocumentOrder = (root: Category, searchString: string): FindResult[] => {
    const results: FindResult[] = [];
    const searchTerms = parseSearchTerms(searchString);

    const visit = (currentCategory: Category) => {
      const nameMatches = searchInNames && checkTextMatch(currentCategory.name, searchTerms);
      const descriptionMatches =
        searchInDescriptions &&
        currentCategory.description &&
        checkTextMatch(currentCategory.description, searchTerms);

      let matchedContents: any[] = [];
      if (searchInContent && currentCategory.contents && currentCategory.contents.length > 0) {
        for (const content of currentCategory.contents) {
          if (!shouldIncludeContent(content)) continue;
          if (
            checkTextMatch(
              contentSearchablePlainText(content.text, content.richTextHtml),
              searchTerms,
            )
          ) {
            matchedContents.push(content);
          }
        }
      }

      if (nameMatches || descriptionMatches || matchedContents.length > 0) {
        results.push({
          category: currentCategory,
          matchedContents: matchedContents.length > 0 ? matchedContents : undefined,
        });
      }

      for (const child of currentCategory.children ?? []) {
        visit(child);
      }
    };

    visit(root);
    return results;
  };

  const applyFindResults = (
    results: FindResult[],
    searchTree: Category | null,
    origin: { categoryId: string | null; contentIndex: number; contentId: string | null },
    options?: { preferSelectedCategoryOnly?: boolean },
  ) => {
    let findIndex = 0;
    let contentIndex = 0;
    let wrapped = false;

    if (options?.preferSelectedCategoryOnly && origin.categoryId) {
      // Semantic: keep score order, but open on the selected category when it has a hit.
      const idx = results.findIndex((r) => r.category.id === origin.categoryId);
      if (idx >= 0) {
        findIndex = idx;
        const matched = results[idx].matchedContents;
        if (matched && matched.length > 0 && origin.contentIndex >= 0) {
          const contents = results[idx].category.contents ?? [];
          const mIdx = matched.findIndex((c) => {
            const p = contents.findIndex((row) => row.id === c.id);
            return p >= origin.contentIndex;
          });
          if (mIdx >= 0) contentIndex = mIdx;
        }
      }
    } else if (searchTree) {
      const start = findStartPosition(
        results,
        searchTree,
        origin.categoryId,
        origin.contentIndex,
      );
      findIndex = start.findIndex;
      contentIndex = start.contentIndex;
      wrapped = start.wrapped;
    }

    setFindResults(results);
    setCurrentFindIndex(findIndex);
    setCurrentContentIndex(contentIndex);
    setFindStarted(true);
    setShowEndWarning(wrapped);
    setShowStartWarning(false);
    setHasWrappedAround(wrapped);
    if (wrapped) {
      setTimeout(() => setShowEndWarning(false), 3000);
    }

    if (results.length === 0) {
      setShowEndWarning(true);
      setTimeout(() => setShowEndWarning(false), 3000);
      return;
    }

    const targetResult = results[findIndex];
    onCategorySelect(targetResult.category);
    onExpandParents(targetResult.category);

    if (targetResult.matchedContents && onContentSelect) {
      const allIds: string[] = [];
      results.forEach((result) => {
        result.matchedContents?.forEach((content) => {
          allIds.push(content.id);
        });
      });
      setCurrentSearchMatchingIds(allIds);
      const content =
        targetResult.matchedContents[contentIndex] ?? targetResult.matchedContents[0];
      onContentSelect(
        targetResult.category.id,
        content.id,
        findSearchString,
        searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString),
        searchMode,
        wholeWord,
        caseSensitive,
        allIds,
      );
    }
  };

  const handleFindSearch = async () => {
    console.log('handleFindSearch called');
    console.log('findSearchString:', findSearchString);
    console.log('searchInNames:', searchInNames);
    console.log('searchInContent:', searchInContent);
    console.log('searchInDescriptions:', searchInDescriptions);
    console.log('searchMode:', searchMode);
    
    if (!findSearchString.trim()) return;

    // Capture pane selection before any await (full-tree fetch must not lose the origin row).
    const searchOrigin = captureSearchOrigin();
    
    if (searchMode === 'semantic') {
      if (!semanticFindEnabled) {
        return;
      }
      // Handle semantic search
      setIsSemanticSearching(true);
      try {
        const rootId = categories[0]?.id;
        const searchTree = rootId ? await fetchCategoryWithChildren(rootId) : null;

        // Get current document name from props or use default
        const currentDocument = documentName || 'default';
        console.log('Performing semantic search for document:', currentDocument);
        
        const response = await api.post('/search/semantic', {
          question: findSearchString
        }, { params: { documentId: currentDocument} });
        
        console.log('Semantic search response:', response.data);
        
        const semanticResults = response.data.results || [];
        const convertedResults: { category: Category; matchedContents?: any[]; score?: number }[] = [];
        
        for (const result of semanticResults) {
          // Parse contentId to extract category information
          // contentId format: "2:39:עדכון 24 ראיונות:﻿אורח חיים יהודי בבית:﻿חגי ישראל:0"
          // Handle both quoted and unquoted contentId strings
          let contentId = result.contentId;
          try {
            // Try to parse as JSON in case it's a quoted string
            const parsed = JSON.parse(contentId);
            if (typeof parsed === 'string') {
              contentId = parsed;
            }
          } catch (e) {
            // If parsing fails, use the original contentId (it's already a clean string)
          }
          
          const contentIdParts = contentId.split(':');
          if (contentIdParts.length >= 4) {
            // Extract category path (everything except the last part which is the content index)
            const categoryPathParts = contentIdParts.slice(2, -1);
            console.log('Looking for category path parts:', categoryPathParts);
            
            // Find the category in the tree by navigating through the path
            const findCategoryByPathParts = (cats: Category[], pathParts: string[]): Category | null => {
              if (pathParts.length === 0) return null;
              
              const currentPart = pathParts[0];
              const remainingParts = pathParts.slice(1);
              
              for (const cat of cats) {
                if (normalizeCategoryName(cat.name) === normalizeCategoryName(currentPart)) {
                  if (remainingParts.length === 0) {
                    // Found the target category
                    return cat;
                  } else {
                    // Continue searching in children
                    if (cat.children) {
                      const found = findCategoryByPathParts(cat.children, remainingParts);
                      if (found) return found;
                    }
                  }
                }
              }
              return null;
            };
            
            const category = findCategoryByPathParts(searchTree ? [searchTree] : categories, categoryPathParts);
            if (category) {
              console.log('Found category:', category.name);
              
              // Find the real content object in the category's contents array
              let realContent = null;
              if (category.contents && category.contents.length > 0) {
                // Extract the content index from the contentId (last part)
                const contentIndex = parseInt(contentIdParts[contentIdParts.length - 1]);
                console.log('Looking for content at index:', contentIndex);
                
                if (contentIndex >= 0 && contentIndex < category.contents.length) {
                  realContent = category.contents[contentIndex];
                  console.log('Found real content:', realContent);
                } else {
                  console.log('Content index out of bounds:', contentIndex, 'vs', category.contents.length);
                }
              }
              
              if (realContent) {
                convertedResults.push({
                  category,
                  matchedContents: [realContent],
                  score: result.score // Store the similarity score
                });
              } else {
                console.log('Real content not found, skipping this result');
              }
            } else {
              console.log('Category not found for path parts:', categoryPathParts);
            }
          }
        }

        // After processing all semantic results, filter by subcases if enabled
        if (filterBySubcases) {
          const selectedSubcases = getSelectedSubcases();
          console.log('Filtering semantic results by subcases:', selectedSubcases);
          const filteredResults =
            selectedSubcases.length === 0
              ? []
              : convertedResults.filter(result => {
                  if (result.matchedContents && result.matchedContents.length > 0) {
                    return result.matchedContents.some(content =>
                      selectedSubcases.includes(content.subcase)
                    );
                  }
                  return false;
                });
          console.log('Filtered semantic results:', filteredResults.length, 'out of', convertedResults.length);
          convertedResults.length = 0;
          convertedResults.push(...filteredResults);
        }
        
        // Within a category, keep content in document order; otherwise keep score order.
        convertedResults.sort((a, b) => {
          if (a.category.id !== b.category.id) return 0;
          if (!a.matchedContents?.[0] || !b.matchedContents?.[0]) return 0;
          if (!a.category.contents) return 0;
          const indexA = a.category.contents.findIndex((c) => c.id === a.matchedContents![0].id);
          const indexB = a.category.contents.findIndex((c) => c.id === b.matchedContents![0].id);
          return indexA - indexB;
        });

        applyFindResults(convertedResults, searchTree, searchOrigin, {
          preferSelectedCategoryOnly: true,
        });
      } catch (error) {
        console.error('Error performing semantic search:', error);
        setShowEndWarning(true);
        setTimeout(() => setShowEndWarning(false), 3000);
      } finally {
        setIsSemanticSearching(false);
      }
      return;
    }
    
    // Load the full document tree so content rows are available (the in-memory tree often omits them).
    const rootId = categories[0]?.id;
    if (!rootId) return;

    setIsTextSearching(true);
    let searchTree: Category;
    try {
      searchTree = await fetchCategoryWithChildren(rootId);
    } catch (error) {
      console.error('Error loading document tree for find:', error);
      setShowEndWarning(true);
      setTimeout(() => setShowEndWarning(false), 3000);
      return;
    } finally {
      setIsTextSearching(false);
    }

    const results = collectMatchesInDocumentOrder(searchTree, findSearchString);
    applyFindResults(results, searchTree, searchOrigin);
  };

  const handleFindNext = () => {
    if (findResults.length === 0) return;
    
    const currentResult = findResults[currentFindIndex];
    
    // If current result has multiple content matches, navigate to next content first
    if (currentResult.matchedContents && currentResult.matchedContents.length > 1) {
      const nextContentIndex = currentContentIndex + 1;
      
      if (nextContentIndex < currentResult.matchedContents.length) {
        // Move to next content in same category
        setCurrentContentIndex(nextContentIndex);
        setShowEndWarning(false);
        setShowStartWarning(false);
        setHasWrappedAround(false);
        
        if (onContentSelect) {
          onContentSelect(currentResult.category.id, currentResult.matchedContents[nextContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
        }
        return;
      } else {
        // No more content in this category, reset content index and move to next category
        setCurrentContentIndex(0);
      }
    }
    
    // Move to next category
    const nextIndex = currentFindIndex + 1;
    
    if (nextIndex >= findResults.length) {
      // Reached the end, automatically continue from the top
      setShowEndWarning(true);
      setHasWrappedAround(true);
      setTimeout(() => setShowEndWarning(false), 3000); // Hide after 3 seconds
      setCurrentFindIndex(0);
      setCurrentContentIndex(0);
      const targetResult = findResults[0];
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
      }
    } else {
      setShowEndWarning(false);
      setShowStartWarning(false);
      setHasWrappedAround(false);
      setCurrentFindIndex(nextIndex);
      setCurrentContentIndex(0);
      const targetResult = findResults[nextIndex];
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
      }
    }
  };

  const handleFindPrevious = () => {
    if (findResults.length === 0) return;
    
    const currentResult = findResults[currentFindIndex];
    
    // If current result has multiple content matches, navigate to previous content first
    if (currentResult.matchedContents && currentResult.matchedContents.length > 1) {
      const prevContentIndex = currentContentIndex - 1;
      
      if (prevContentIndex >= 0) {
        // Move to previous content in same category
        setCurrentContentIndex(prevContentIndex);
        setShowEndWarning(false);
        setShowStartWarning(false);
        setHasWrappedAround(false);
        
        if (onContentSelect) {
          onContentSelect(currentResult.category.id, currentResult.matchedContents[prevContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
        }
        return;
      } else {
        // No more content in this category, move to previous category and set to last content
        const prevIndex = currentFindIndex - 1;
        if (prevIndex >= 0) {
          const prevResult = findResults[prevIndex];
          const lastContentIndex = prevResult.matchedContents ? prevResult.matchedContents.length - 1 : 0;
          setCurrentFindIndex(prevIndex);
          setCurrentContentIndex(lastContentIndex);
          onCategorySelect(prevResult.category);
          onExpandParents(prevResult.category);
          if (prevResult.matchedContents && onContentSelect) {
            onContentSelect(prevResult.category.id, prevResult.matchedContents[lastContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
          return;
        }
      }
    }
    
    // Move to previous category
    const prevIndex = currentFindIndex - 1;
    
    if (prevIndex < 0) {
      // Reached the beginning, automatically continue from the end
      setShowStartWarning(true);
      setHasWrappedAround(true);
      setTimeout(() => setShowStartWarning(false), 3000); // Hide after 3 seconds
      const lastIndex = findResults.length - 1;
      setCurrentFindIndex(lastIndex);
      const targetResult = findResults[lastIndex];
      const lastContentIndex = targetResult.matchedContents ? targetResult.matchedContents.length - 1 : 0;
      setCurrentContentIndex(lastContentIndex);
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        onContentSelect(targetResult.category.id, targetResult.matchedContents[lastContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
      }
    } else {
      setShowEndWarning(false);
      setShowStartWarning(false);
      setHasWrappedAround(false);
      setCurrentFindIndex(prevIndex);
      const targetResult = findResults[prevIndex];
      const lastContentIndex = targetResult.matchedContents ? targetResult.matchedContents.length - 1 : 0;
      setCurrentContentIndex(lastContentIndex);
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        onContentSelect(targetResult.category.id, targetResult.matchedContents[lastContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
      }
    }
  };

  // Helper function to get all matching content IDs from all search results
  const getAllMatchingContentIds = (): string[] => {
    const allIds: string[] = [];
    findResults.forEach(result => {
      if (result.matchedContents) {
        result.matchedContents.forEach(content => {
          allIds.push(content.id);
        });
      }
    });
    return allIds;
  };

  // Helper function to calculate total matches including content matches
  const getTotalMatches = () => {
    let total = 0;
    findResults.forEach(result => {
      if (result.matchedContents) {
        total += result.matchedContents.length;
      } else {
        total += 1; // Category match (name or description)
      }
    });
    return total;
  };

  // Helper function to get current match number
  const getCurrentMatchNumber = () => {
    let current = 0;
    for (let i = 0; i < currentFindIndex; i++) {
      const result = findResults[i];
      if (result.matchedContents) {
        current += result.matchedContents.length;
      } else {
        current += 1;
      }
    }
    // Add current category's matches
    const currentResult = findResults[currentFindIndex];
    if (currentResult.matchedContents) {
      current += currentContentIndex + 1;
    } else {
      current += 1;
    }
    return current;
  };

  const handleDialogClose = () => {
    // Dismiss open tooltips before unmount so portal poppers are not left on screen.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setFindSearchString('');
    setFindResults([]);
    setCurrentFindIndex(0);
    setCurrentContentIndex(0);
    setFindStarted(false);
    setShowEndWarning(false);
    setShowStartWarning(false);
    setHasWrappedAround(false);
    setSearchInDescriptions(false);
    setSearchInNames(true);
    setSearchInContent(true);
    setSearchMode('any');
    setWholeWord(false);
    setCaseSensitive(false);
    setCurrentSearchMatchingIds([]);
    setFilterBySubcases(false);
    setSelectedSubcaseGroups([]);
    setSelectedIndividualSubcases([]);
    findFilterSubcasesBaselineRef.current = null;
    setSearchOptionsCollapsed(false);
    setSubcaseSelectionCollapsed(false);
    setIsSemanticSearching(false);
    setIsTextSearching(false);
    // Reset position to initial top-right position
    setDialogPosition({ x: Math.max(0, window.innerWidth - 350), y: 80 });
    onClose();
  };

  // Dialog drag handlers
  const handleDialogMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    
    // Check if the click is on the header area
    const isHeader = target.closest('[style*="background-color: rgb(240, 240, 240)"]') || 
                    target.closest('[style*="background-color: #f0f0f0"]') ||
                    target.style.backgroundColor === 'rgb(240, 240, 240)' ||
                    target.style.backgroundColor === '#f0f0f0';
    
    if (isHeader) {
      e.preventDefault();
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleDialogMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep dialog within viewport bounds
      const maxX = window.innerWidth - 400; // dialog width
      const maxY = window.innerHeight - 300; // dialog height
      
      setDialogPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleDialogMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDialogMouseMove);
      document.addEventListener('mouseup', handleDialogMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleDialogMouseMove);
        document.removeEventListener('mouseup', handleDialogMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  useEffect(() => {
    if (!semanticFindEnabled && searchMode === 'semantic') {
      setSearchMode('any');
    }
  }, [semanticFindEnabled, searchMode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      // Escape always closes Find, including when the search field is focused.
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleDialogClose();
        return;
      }
      
      // Prevent other shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // Enter in the search field still runs Find.
        if (e.key === 'Enter') {
          e.preventDefault();
          void handleFindSearch();
        }
        return;
      }
      
      // Ctrl+F or Cmd+F: Focus search input
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Enter"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
      
      // Enter: Perform search
      if (e.key === 'Enter') {
        e.preventDefault();
        handleFindSearch();
      }
      
      // F3 or Ctrl+G: Find next
      if (e.key === 'F3' || ((e.ctrlKey || e.metaKey) && e.key === 'g')) {
        e.preventDefault();
        handleFindNext();
      }
      
      // Shift+F3 or Ctrl+Shift+G: Find previous
      if ((e.shiftKey && e.key === 'F3') || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G')) {
        e.preventDefault();
        handleFindPrevious();
      }
      
      // Ctrl+Shift+F3 or Ctrl+Shift+Alt+G: Next category
      if ((e.ctrlKey && e.shiftKey && e.key === 'F3') || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.altKey && e.key === 'G')) {
        e.preventDefault();
        const nextIndex = currentFindIndex + 1;
        if (nextIndex >= findResults.length) {
          setCurrentFindIndex(0);
          setCurrentContentIndex(0);
          const targetResult = findResults[0];
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          if (targetResult.matchedContents && onContentSelect) {
            onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
        } else {
          setCurrentFindIndex(nextIndex);
          setCurrentContentIndex(0);
          const targetResult = findResults[nextIndex];
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          if (targetResult.matchedContents && onContentSelect) {
            onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
        }
      }
      
      // Ctrl+Alt+F3 or Ctrl+Alt+G: Previous category
      if ((e.ctrlKey && e.altKey && e.key === 'F3') || ((e.ctrlKey || e.metaKey) && e.altKey && e.key === 'G')) {
        e.preventDefault();
        const prevIndex = currentFindIndex - 1;
        if (prevIndex < 0) {
          const lastIndex = findResults.length - 1;
          setCurrentFindIndex(lastIndex);
          setCurrentContentIndex(0);
          const targetResult = findResults[lastIndex];
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          if (targetResult.matchedContents && onContentSelect) {
            onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
        } else {
          setCurrentFindIndex(prevIndex);
          setCurrentContentIndex(0);
          const targetResult = findResults[prevIndex];
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          if (targetResult.matchedContents && onContentSelect) {
            onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, findResults.length, currentFindIndex]);

  if (!open) return null;

  return (
    <Box
      data-testid="find-in-document-panel"
      sx={(t) => ({
        position: 'fixed',
        left: dialogPosition.x,
        top: dialogPosition.y,
        // Below MUI modals (1300) so real dialogs are never trapped under Find.
        zIndex: t.zIndex.modal - 1,
        backgroundColor: t.palette.background.paper,
        color: t.palette.text.primary,
        borderRadius: 2,
        boxShadow: t.shadows[8],
        border: `1px solid ${t.palette.divider}`,
        p: 1,
        minWidth: 280,
        maxWidth: 350,
        userSelect: 'none',
      })}
    >
      {/* Draggable Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 1,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' }
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
          const rect = e.currentTarget.getBoundingClientRect();
          setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          });
        }}
      >
        <Typography variant="subtitle1" sx={{ flexGrow: 1, userSelect: 'none', fontSize: '0.9rem' }}>
          Find in Document
        </Typography>
        <FindTooltip title={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Keyboard Shortcuts:</Typography>
            <Typography variant="caption" component="div">Ctrl+F / Cmd+F: Focus search</Typography>
            <Typography variant="caption" component="div">Enter: Search</Typography>
            <Typography variant="caption" component="div">F3 / Ctrl+G: Find next content</Typography>
            <Typography variant="caption" component="div">Shift+F3 / Ctrl+Shift+G: Find previous content</Typography>
            <Typography variant="caption" component="div">Ctrl+Shift+F3 / Ctrl+Shift+Alt+G: Next category</Typography>
            <Typography variant="caption" component="div">Ctrl+Alt+F3 / Ctrl+Alt+G: Previous category</Typography>
            <Typography variant="caption" component="div">Escape: Close dialog</Typography>
          </Box>
        }>
          <IconButton size="small" sx={{ mr: 0.5 }}>
            <HelpIcon fontSize="small" />
          </IconButton>
        </FindTooltip>
        <FindTooltip title={getTooltipTextForState('find-close', true)}>
          <IconButton 
            size="small" 
            onClick={handleDialogClose}
            sx={{ userSelect: 'none' }}
          >
            <CloseIcon />
          </IconButton>
        </FindTooltip>
      </Box>
      
      {/* Dialog Content */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={
            searchMode === 'exact' 
              ? 'Exact phrase...' 
              : searchMode === 'all'
              ? 'All words...'
              : 'Any words...'
          }
          value={findSearchString}
          onChange={(e) => setFindSearchString(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleFindSearch()}
          sx={{ flexGrow: 1 }}
          autoFocus
          title={getTooltipTextForState('find-search-input', true)}
        />
        <FindTooltip title={getTooltipTextForState('find-search', true)}>
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={handleFindSearch}
              disabled={!findSearchString.trim() || isSemanticSearching || isTextSearching}
              sx={{ height: '40px' }} // Match TextField small size height
            >
              {isSemanticSearching || isTextSearching ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Searching...
                </Box>
              ) : (
                'Find'
              )}
            </Button>
          </span>
        </FindTooltip>
      </Box>
      
      {/* Search Options */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Search options
          </Typography>
          <FindTooltip title={getTooltipTextForState('find-search-options-toggle', true)}>
            <IconButton
              size="small"
              data-testid="find-search-options-toggle"
              aria-label={searchOptionsCollapsed ? 'Expand search options' : 'Collapse search options'}
              onClick={() => setSearchOptionsCollapsed(!searchOptionsCollapsed)}
              sx={{ p: 0.5 }}
            >
              {searchOptionsCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </IconButton>
          </FindTooltip>
        </Box>
        
        {!searchOptionsCollapsed && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Search in:
            </Typography>
                <FindTooltip title={getTooltipTextForState('find-search-names', true)}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={searchInNames}
                          onChange={(e) => setSearchInNames(e.target.checked)}
                          size="small"
                          disabled={searchMode === 'semantic'}
                          inputProps={{ 'data-testid': 'find-scope-names' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label={
                        <FindTooltip title={getTooltipTextForState('find-search-names', true)}>
                          <span>Category names</span>
                        </FindTooltip>
                      }
                    />
                  </span>
                </FindTooltip>
                <FindTooltip title={getTooltipTextForState('find-search-content', true)}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={searchInContent}
                          onChange={(e) => setSearchInContent(e.target.checked)}
                          size="small"
                          disabled={searchMode === 'semantic'}
                          inputProps={{ 'data-testid': 'find-scope-content' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label="Content"
                    />
                  </span>
                </FindTooltip>
                <FindTooltip title={getTooltipTextForState('find-search-descriptions', true)}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={searchInDescriptions}
                          onChange={(e) => setSearchInDescriptions(e.target.checked)}
                          size="small"
                          disabled={searchMode === 'semantic'}
                          inputProps={{ 'data-testid': 'find-scope-descriptions' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label="Descriptions"
                    />
                  </span>
                </FindTooltip>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Match options:
              </Typography>
              <FindTooltip title={getTooltipTextForState('find-whole-word', true)}>
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={wholeWord}
                        onChange={(e) => setWholeWord(e.target.checked)}
                        size="small"
                        disabled={searchMode === 'semantic'}
                      />
                    }
                    label="Whole word only"
                  />
                </span>
              </FindTooltip>
              <FindTooltip title={getTooltipTextForState('find-case-sensitive', true)}>
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={caseSensitive}
                        onChange={(e) => setCaseSensitive(e.target.checked)}
                        size="small"
                        disabled={searchMode === 'semantic'}
                      />
                    }
                    label="Case sensitive"
                  />
                </span>
              </FindTooltip>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Search mode:
              </Typography>
              <RadioGroup
                value={searchMode}
                onChange={(e) => setSearchMode(e.target.value as 'any' | 'all' | 'exact' | 'semantic')}
                row
              >
                <FindTooltip title={getTooltipTextForState('find-search-mode', true)}>
                  <span>
                    <FormControlLabel
                      value="any"
                      onClick={() => setSearchMode('any')}
                      control={
                        <Radio
                          size="small"
                          inputProps={{ 'data-testid': 'find-mode-any' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label="Any word (OR)"
                    />
                  </span>
                </FindTooltip>
                <FindTooltip title={getTooltipTextForState('find-search-mode', true)}>
                  <span>
                    <FormControlLabel
                      value="all"
                      onClick={() => setSearchMode('all')}
                      control={
                        <Radio
                          size="small"
                          inputProps={{ 'data-testid': 'find-mode-all' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label="All words (AND)"
                    />
                  </span>
                </FindTooltip>
                <FindTooltip title={getTooltipTextForState('find-search-mode', true)}>
                  <span>
                    <FormControlLabel
                      value="exact"
                      onClick={() => setSearchMode('exact')}
                      control={
                        <Radio
                          size="small"
                          inputProps={{ 'data-testid': 'find-mode-exact' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label="Exact phrase"
                    />
                  </span>
                </FindTooltip>
                {semanticFindEnabled ? (
                <FindTooltip title={getTooltipTextForState('semantic-search', true)}>
                  <span>
                    <FormControlLabel
                      value="semantic"
                      onClick={() => setSearchMode('semantic')}
                      control={
                        <Radio
                          size="small"
                          inputProps={{ 'data-testid': 'find-mode-semantic' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AiSparklesIcon sx={{ fontSize: '0.875rem' }} />
                          Semantic
                        </Box>
                      }
                    />
                  </span>
                </FindTooltip>
                ) : null}
              </RadioGroup>
            </Box>

            {searchMode === 'semantic' && semanticFindEnabled && (
              <Box sx={{ mt: 1 }}>
                <FindTooltip title={getTooltipTextForState('find-hybrid-semantic-ranking', true)}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hybridSemanticRanking !== false}
                          onChange={async (e) => {
                            const checked = e.target.checked;
                            updateSettings({ hybridSemanticRanking: checked });
                            try {
                              await api.put('/search/settings', {
                                search: { hybridSemanticRanking: checked },
                              });
                            } catch (error) {
                              console.error('Error updating find settings:', error);
                            }
                          }}
                          size="small"
                          inputProps={{ 'data-testid': 'find-hybrid-ranking' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label="Prefer exact words (hybrid ranking)"
                    />
                  </span>
                </FindTooltip>
                <FindTooltip title={getTooltipTextForState('find-show-similarity-score', true)}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={findShowSimilarityScore}
                          onChange={async (e) => {
                            const checked = e.target.checked;
                            updateSettings({ findShowSimilarityScore: checked });
                            try {
                              await api.put('/search/settings', {
                                search: { findShowSimilarityScore: checked },
                              });
                            } catch (error) {
                              console.error('Error updating find settings:', error);
                            }
                          }}
                          size="small"
                          inputProps={{ 'data-testid': 'find-show-similarity-scores' } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      }
                      label="Show similarity scores"
                    />
                  </span>
                </FindTooltip>
              </Box>
            )}
            
            {/* Subcase Filtering */}
            {documentSubcases.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Filter by subcases:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <FindTooltip title={getTooltipTextForState('find-subcase-filter', true)}>
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterBySubcases}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFilterBySubcases(isChecked);
                              findFilterSubcasesBaselineRef.current = null;
                              if (isChecked) {
                                setSelectedSubcaseGroups([]);
                                setSelectedIndividualSubcases([...documentSubcases]);
                              } else {
                                setSelectedSubcaseGroups([]);
                                setSelectedIndividualSubcases([]);
                              }
                            }}
                            size="small"
                          />
                        }
                        label="Filter by subcases"
                      />
                    </span>
                  </FindTooltip>
                  {filterBySubcases && (
                    <FindTooltip title={getTooltipTextForState('find-subcase-selection-toggle', true)}>
                      <IconButton
                        size="small"
                        onClick={() => setSubcaseSelectionCollapsed(!subcaseSelectionCollapsed)}
                        sx={{ p: 0.5 }}
                      >
                        {subcaseSelectionCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                      </IconButton>
                    </FindTooltip>
                  )}
                </Box>
                
                {filterBySubcases && !subcaseSelectionCollapsed && (
                  <Box sx={{ mt: 1, ml: 3 }}>
                    {/* Subcase Groups */}
                    {subcaseGroups.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Subcase Groups:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {subcaseGroups.map(group => (
                            <Chip
                              key={group.name}
                              label={group.name}
                              size="small"
                              color={selectedSubcaseGroups.includes(group.name) ? 'primary' : 'default'}
                              onClick={() => handleFindSubcaseGroupSelect(group.name)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Individual Subcases */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Individual Subcases:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedIndividualSubcases.length === documentSubcases.length}
                              indeterminate={
                                selectedIndividualSubcases.length > 0 &&
                                selectedIndividualSubcases.length < documentSubcases.length
                              }
                              onChange={e => {
                                findFilterSubcasesBaselineRef.current = null;
                                if (e.target.checked) {
                                  setSelectedIndividualSubcases([...documentSubcases]);
                                  setSelectedSubcaseGroups([]);
                                } else {
                                  setSelectedIndividualSubcases([]);
                                  setSelectedSubcaseGroups([]);
                                }
                              }}
                              size="small"
                            />
                          }
                          label="Select All"
                        />
                        <InvertSelectionButton
                          disabled={documentSubcases.length === 0}
                          onClick={() => {
                            findFilterSubcasesBaselineRef.current = null;
                            setSelectedSubcaseGroups([]);
                            setSelectedIndividualSubcases(prev =>
                              invertSubcaseSelection(documentSubcases, prev),
                            );
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          maxHeight: 120,
                          overflow: 'auto',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        {documentSubcases.map((subcase: string) => (
                          <FormControlLabel
                            key={subcase}
                            control={
                              <Checkbox
                                checked={selectedIndividualSubcases.includes(subcase)}
                                onChange={(e) => {
                                  setSelectedIndividualSubcases(prev =>
                                    e.target.checked
                                      ? [...prev, subcase]
                                      : prev.filter(s => s !== subcase)
                                  );
                                }}
                                size="small"
                              />
                            }
                            label={subcase}
                            sx={{ display: 'block', mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                
                {/* Selected subcases summary - always show when filter is active */}
                {filterBySubcases && (() => {
                  const selectedSubcases = getSelectedSubcases();
                  if (selectedSubcases.length > 0) {
                    return (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 3 }}>
                        Filtering by {selectedSubcases.length} subcase{selectedSubcases.length !== 1 ? 's' : ''}: {selectedSubcases.slice(0, 3).join(', ')}
                        {selectedSubcases.length > 3 && ` +${selectedSubcases.length - 3} more`}
                      </Typography>
                    );
                  }
                  return null;
                })()}
              </Box>
            )}
          </>
        )}
      </Box>
      
      {findStarted && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {findResults.length > 0 
              ? `Found ${getTotalMatches()} match(es) - ${getCurrentMatchNumber()} of ${getTotalMatches()}`
              : 'No results found'
            }
            {findResults.length > 0 && (
              <span style={{ fontSize: '0.75em', opacity: 0.7 }}>
                {' '}({searchMode === 'any' ? 'OR' : searchMode === 'all' ? 'AND' : searchMode === 'exact' ? 'Exact' : 'Semantic'})
                {caseSensitive && ' • Case sensitive'}
              </span>
            )}
          </Typography>
          {findResults.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Current: {findResults[currentFindIndex].category.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(() => {
                  const matchedContents = findResults[currentFindIndex].matchedContents;
                  if (matchedContents) {
                    return `Match found in content${matchedContents.length > 1 ? ` (${currentContentIndex + 1} of ${matchedContents.length})` : ''}`;
                  } else if (searchInDescriptions && findResults[currentFindIndex].category.description && 
                    checkTextMatch(findResults[currentFindIndex].category.description || '', parseSearchTerms(findSearchString))) {
                    return 'Match found in description';
                  } else if (searchInNames && checkTextMatch(findResults[currentFindIndex].category.name, parseSearchTerms(findSearchString))) {
                    return 'Match found in name';
                  } else {
                    return 'Match found';
                  }
                })()}
                {/* Show similarity score for semantic search */}
                {searchMode === 'semantic' && findShowSimilarityScore && findResults[currentFindIndex].score !== undefined && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                    Similarity: {findResults[currentFindIndex].score!.toFixed(1)}%
                  </Typography>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      
      {/* Warning messages */}
      {showEndWarning && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.22),
            border: `1px solid ${alpha(theme.palette.warning.dark, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
          }}
        >
          <Typography variant="body2" color="warning.dark">
            {hasWrappedAround ? 'Continued from the top.' : 'Reached the end of search results.'}
          </Typography>
        </Box>
      )}
      
      {showStartWarning && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.22),
            border: `1px solid ${alpha(theme.palette.warning.dark, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
          }}
        >
          <Typography variant="body2" color="warning.dark">
            {hasWrappedAround ? 'Continued from the bottom.' : 'Reached the beginning of search results.'}
          </Typography>
        </Box>
      )}
      
      {findResults.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Content Navigation - only show when content search is enabled or semantic search is used */}
          {(searchInContent || searchMode === 'semantic') && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <FindTooltip title={getTooltipTextForState('find-previous', true)}>
                <span>
                  <Button
                    size="small"
                    onClick={handleFindPrevious}
                    disabled={(() => {
                      if (findResults.length > 1) return false;
                      const matchedContents = findResults[currentFindIndex].matchedContents;
                      return !matchedContents || matchedContents.length <= 1;
                    })()}
                    startIcon={<KeyboardArrowUpIcon />}
                    variant="outlined"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      width: '100px'
                    }}
                  >
                    Prev Content
                  </Button>
                </span>
              </FindTooltip>
              <FindTooltip title={getTooltipTextForState('find-next', true)}>
                <span>
                  <Button
                    size="small"
                    onClick={handleFindNext}
                    disabled={(() => {
                      if (findResults.length > 1) return false;
                      const matchedContents = findResults[currentFindIndex].matchedContents;
                      return !matchedContents || matchedContents.length <= 1;
                    })()}
                    endIcon={<KeyboardArrowDownIcon />}
                    variant="outlined"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      width: '100px'
                    }}
                  >
                    Next Content
                  </Button>
                </span>
              </FindTooltip>
            </Box>
          )}
          
          {/* Category Navigation */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <FindTooltip title={getTooltipTextForState('find-previous-category', true)}>
              <span>
                <Button
                  size="small"
                  onClick={() => {
                    // Navigate to previous category
                    const prevIndex = currentFindIndex - 1;
                    if (prevIndex < 0) {
                      // Wrap around to the end
                      const lastIndex = findResults.length - 1;
                      setCurrentFindIndex(lastIndex);
                      setCurrentContentIndex(0);
                      const targetResult = findResults[lastIndex];
                      onCategorySelect(targetResult.category);
                      onExpandParents(targetResult.category);
                      if (targetResult.matchedContents && onContentSelect) {
                        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
                      }
                    } else {
                      setCurrentFindIndex(prevIndex);
                      setCurrentContentIndex(0);
                      const targetResult = findResults[prevIndex];
                      onCategorySelect(targetResult.category);
                      onExpandParents(targetResult.category);
                      if (targetResult.matchedContents && onContentSelect) {
                        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
                      }
                    }
                  }}
                  disabled={findResults.length <= 1}
                  startIcon={<KeyboardArrowUpIcon />}
                  variant="contained"
                  color="primary"
                  sx={{ 
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem',
                    width: '100px'
                  }}
                >
                  Prev Category
                </Button>
              </span>
            </FindTooltip>
            <FindTooltip title={getTooltipTextForState('find-next-category', true)}>
              <span>
                <Button
                  size="small"
                  onClick={() => {
                    // Navigate to next category
                    const nextIndex = currentFindIndex + 1;
                    if (nextIndex >= findResults.length) {
                      // Wrap around to the beginning
                      setCurrentFindIndex(0);
                      setCurrentContentIndex(0);
                      const targetResult = findResults[0];
                      onCategorySelect(targetResult.category);
                      onExpandParents(targetResult.category);
                      if (targetResult.matchedContents && onContentSelect) {
                        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
                      }
                    } else {
                      setCurrentFindIndex(nextIndex);
                      setCurrentContentIndex(0);
                      const targetResult = findResults[nextIndex];
                      onCategorySelect(targetResult.category);
                      onExpandParents(targetResult.category);
                      if (targetResult.matchedContents && onContentSelect) {
                        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
                      }
                    }
                  }}
                  disabled={findResults.length <= 1}
                  endIcon={<KeyboardArrowDownIcon />}
                  variant="contained"
                  color="primary"
                  sx={{ 
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem',
                    width: '100px'
                  }}
                >
                  Next Category
                </Button>
              </span>
            </FindTooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FindCategoryDialog;